import { useEffect, useState } from 'react';

/**
 * Names for the ISO 3166-2 codes a holiday's `region` carries.
 *
 * The service sends the code and nothing else - `ES-AN`, `DE-BW` - because the feed it imports
 * publishes no names, and nothing in the browser resolves one: `Intl.DisplayNames` stops at ISO
 * 3166-1 countries and throws on anything below them. So the names are shipped with the bundle,
 * generated from CLDR into `src/app/subdivisions.json` by `npm run generate:subdivisions`.
 *
 * Read through this module rather than from the file, for two reasons. The table is ~95 KB and
 * only the Holidays administration screen wants it, so it is fetched as its own chunk on first
 * use instead of sitting in the bundle every session pays for. And a code with no name in it is
 * ordinary - a subdivision newer than the CLDR release pinned in the generator, or one the
 * service invented - so every lookup here falls back to the code rather than to nothing.
 */

/** Subdivision names by country: `{ ES: { AN: 'Andalusia' } }`, as the generated file is written. */
export type SubdivisionTable = Record<string, Record<string, string>>;

/** A subdivision as the UI offers it: the full code to store, and what to show for it. */
export interface Subdivision {
  /** ISO 3166-2, as `Holiday.region` holds it. */
  code: string;
  name: string;
}

/**
 * A full ISO 3166-2 code: a country, a hyphen, and one to three characters of subdivision.
 * The service matches these case-insensitively, so everything here is compared uppercased.
 */
export const SUBDIVISION_CODE = /^[A-Z]{2}-[A-Z0-9]{1,3}$/;

/** The table before it has arrived. A constant so a component does not re-render onto a new `{}`. */
const EMPTY: SubdivisionTable = {};

/** `ES-AN` -> `ES`. */
export const countryOfSubdivision = (code: string): string => code.trim().slice(0, 2).toUpperCase();

/** Whether `code` is a well-formed subdivision of `alpha2` - what the Holidays form validates. */
export const isSubdivisionOf = (code: string, alpha2: string | undefined): boolean =>
  SUBDIVISION_CODE.test(code.trim().toUpperCase()) &&
  countryOfSubdivision(code) === (alpha2 ?? '').toUpperCase();

/**
 * What to show for a code. The code itself when the table has no name for it, which is the
 * designed outcome rather than a failure - a reader can still act on `ES-XX`.
 */
export const subdivisionName = (code: string, table: SubdivisionTable): string => {
  const normalised = code.trim().toUpperCase();
  return table[normalised.slice(0, 2)]?.[normalised.slice(3)] ?? normalised;
};

/** Every subdivision of one country, by name, for a picker to offer. */
export const subdivisionsOf = (alpha2: string | undefined, table: SubdivisionTable): Subdivision[] => {
  const country = (alpha2 ?? '').toUpperCase();
  const names = table[country];
  if (!names) return [];

  return Object.entries(names)
    .map(([subdivision, name]) => ({ code: `${country}-${subdivision}`, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

// Module-level so the chunk is requested once per session however many views ask for it, and
// so a second mount renders the names immediately instead of flashing codes again.
let loaded: SubdivisionTable | null = null;
let inFlight: Promise<SubdivisionTable> | null = null;

/** Fetches the table, or hands back the one already fetched. Never rejects — see `useSubdivisions`. */
export const loadSubdivisions = (): Promise<SubdivisionTable> => {
  if (loaded) return Promise.resolve(loaded);

  inFlight ??= import('@app/subdivisions.json')
    .then((module) => {
      loaded = (module.default ?? module) as SubdivisionTable;
      return loaded;
    })
    .catch((error) => {
      // A chunk that will not load costs the reader names, not the page: every lookup above
      // falls back to the code. Logged rather than surfaced, and retried by the next mount.
      console.error('[Subdivisions] Could not load the subdivision names.', error);
      inFlight = null;
      return EMPTY;
    });

  return inFlight;
};

/**
 * The table, empty until the chunk arrives. Rendering against the empty one is correct rather
 * than provisional: it shows codes, which is what an unnamed subdivision shows anyway.
 */
export const useSubdivisions = (): SubdivisionTable => {
  const [table, setTable] = useState<SubdivisionTable>(loaded ?? EMPTY);

  useEffect(() => {
    if (loaded) return;

    let mounted = true;
    void loadSubdivisions().then((next) => {
      if (mounted) setTable(next);
    });

    return () => {
      mounted = false;
    };
  }, []);

  return table;
};
